/**
 * v11.6 V116-1-4 / V116-1-5 (ADR-0115): a control's sources on system control rows.
 *   - SourceTagLine: one dim line of the laws and frameworks that drive the control (counting sources only), plus a
 *     "Decision needed" marker when a contested law waits on the workspace.
 *   - SourcesSection: the expanded list, grouped Laws · Frameworks, each with a status and its link.
 * Labels come from what the app knows (law short name, citation, standing); no narrative in the chrome. A contested
 * law ("Decision needed", "Not counted") links to the AI Regulatory scope decisions (v11.7 V117-1-10).
 */
import { Link, useLocation } from 'react-router-dom';
import { Badge, Button } from '@aigovops/design-system';
import type { ControlCaseSource, ControlFrameworkSource, ControlLawSource, ControlSource } from '../../lib/api';
import { fmtDate } from '../../lib/format';

const isLaw = (s: ControlSource): s is ControlLawSource => s.kind === 'law';
const isFramework = (s: ControlSource): s is ControlFrameworkSource => s.kind === 'framework';
const isCase = (s: ControlSource): s is ControlCaseSource => s.kind === 'case';

const EFFECT_LABEL: Record<ControlCaseSource['effect'], string> = { establishes: 'Establishes', broadens: 'Broadens', narrows: 'Narrows', informs: 'Informs' };
const STANDING_LABEL: Record<string, string> = { final: 'Final', pending: 'Pending', settled: 'Settled', stayed: 'Stayed', vacated: 'Vacated · does not count', annulled: 'Annulled · does not count' };

/** "Pending · illustrative", "Final · binding", "Vacated · does not count". */
export function caseStatusLabel(c: ControlCaseSource): string {
  const st = c.standing ? STANDING_LABEL[c.standing] ?? c.standing : null;
  if (!c.counts) return st ?? 'Does not count';
  return [st, c.weight].filter(Boolean).join(' · ');
}

/** Laws that apply to this system (a law tagged with other systems only is not this system's). */
export function sourcesForSystem(sources: ControlSource[] | undefined, systemId: string): ControlSource[] | undefined {
  if (!sources) return sources;
  return sources.filter((s) => !isLaw(s) || !s.system_ids || s.system_ids.length === 0 || s.system_ids.includes(systemId));
}

/** The status a reader sees for a law. Every not-counting state says so in words. */
export function lawStatusLabel(s: ControlLawSource): string {
  if (s.applicability === 'decision_needed') return 'Decision needed · not counted yet';
  if (s.applicability === 'does_not_apply') return 'Not counted';
  switch (s.standing) {
    case 'upcoming': return s.effective_date ? `From ${fmtDate(s.effective_date)}` : 'Upcoming';
    case 'stayed': return s.stay_kind === 'standstill' ? 'Enforcement paused' : s.stay_kind === 'court_order' ? 'Stayed by court' : 'Stayed';
    case 'vacated': return 'Vacated · does not count';
    case 'annulled': return 'Annulled · does not count';
    case 'pending': return 'Pending';
    case 'settled': return 'Settled';
    default: return 'In force';
  }
}

/** "audit interval months 12" → a short, readable parameter line; nested values are skipped. */
export function parameterText(p: Record<string, unknown> | null | undefined): string | null {
  if (!p) return null;
  const parts = Object.entries(p)
    .filter(([, v]) => ['string', 'number', 'boolean'].includes(typeof v))
    .map(([k, v]) => `${k.replace(/_/g, ' ')}: ${String(v)}`);
  return parts.length ? parts.join(' · ') : null;
}

const needsDecision = (s: ControlLawSource) => s.applicability === 'decision_needed';
const isContested = (s: ControlLawSource) => s.applicability === 'decision_needed' || s.applicability === 'does_not_apply';
/** Attention first, then counting, then not counting; by name within each. */
const rank = (s: ControlLawSource) => (needsDecision(s) ? 0 : s.counts ? 1 : 2);

/** AI Regulatory scope decisions for one contested rule (v11.7 V117-1-10); `from` = the page to come back to. */
export function decisionHref(slug: string, ruleId: string, from?: string | null, fromLabel?: string | null): string {
  const base = `/w/${encodeURIComponent(slug)}/app/programs/ai-regulatory/scope?view=decisions&rule=${encodeURIComponent(ruleId)}`;
  if (!from) return base;
  return `${base}&from=${encodeURIComponent(from)}${fromLabel ? `&from_label=${encodeURIComponent(fromLabel)}` : ''}`;
}

/** One dim line: counting laws, then frameworks, plus a marker when a decision is needed. */
export function SourceTagLine({ sources, fallback }: { sources: ControlSource[] | undefined; fallback: string[] }) {
  const laws = (sources ?? []).filter(isLaw);
  const names = sources
    ? [...new Set([...laws.filter((s) => s.counts).map((s) => s.short_name), ...sources.filter(isFramework).map((s) => s.short_name)])]
    : fallback;
  const pending = laws.filter(needsDecision).length;
  const cases = sources ? sources.filter(isCase).filter((c) => c.counts).length : 0;
  if (cases) names.push(`${cases} case${cases === 1 ? '' : 's'}`);
  if (names.length === 0 && pending === 0) return null;
  return (
    <span style={{ color: 'var(--text-dim)', fontSize: 'var(--font-size-3xs)', display: 'block', marginTop: '2px' }}>
      {names.join(' · ')}
      {pending > 0 ? <span style={{ color: 'var(--accent-soft-text, var(--accent))' }}>{names.length ? ' · ' : ''}Decision needed</span> : null}
    </span>
  );
}

const linkStyle: React.CSSProperties = { color: 'var(--accent-soft-text, var(--accent))', textDecoration: 'underline', textUnderlineOffset: '2px', fontWeight: 500 };
const row: React.CSSProperties = { display: 'flex', gap: 'var(--space-2)', alignItems: 'baseline', flexWrap: 'wrap', minHeight: '24px' };
const groupHead: React.CSSProperties = { margin: 0, fontSize: 'var(--font-size-3xs)', fontWeight: 500, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.04em' };
const list: React.CSSProperties = { listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 'var(--space-1)' };

function ExternalName({ name, url }: { name: string; url: string | null }) {
  return url ? (
    <a href={url} target="_blank" rel="noopener noreferrer" style={linkStyle}>
      {name}<span aria-hidden="true"> ↗</span><span className="sr-only"> (opens in a new tab)</span>
    </a>
  ) : <span style={{ color: 'var(--text)', fontWeight: 500 }}>{name}</span>;
}

function LawStatus({ s, slug, back }: { s: ControlLawSource; slug: string; back?: { from: string; fromLabel?: string } | null }) {
  const label = lawStatusLabel(s);
  const here = useLocation();
  if (isContested(s)) {
    return (
      <Link to={decisionHref(slug, s.rule_id, back?.from ?? `${here.pathname}${here.search}`, back?.fromLabel)} style={linkStyle}>
        <Badge tone={needsDecision(s) ? 'accent' : 'neutral'}>{label}</Badge>
      </Link>
    );
  }
  return <Badge>{label}</Badge>;
}

export function SourcesSection({ sources, loading, error, onRetry, slug, headingId, back }: {
  sources: ControlSource[] | undefined;
  loading: boolean;
  error?: string | null;
  onRetry?: () => void;
  slug: string;
  /** Unique per row, so each expanded row's section has its own name. */
  headingId: string;
  /** Where the decision page's "Back to …" link returns, and its label (default: this page, "the control"). */
  back?: { from: string; fromLabel?: string } | null;
}) {
  const laws = (sources ?? []).filter(isLaw).sort((a, b) => rank(a) - rank(b) || a.short_name.localeCompare(b.short_name));
  const frameworks = (sources ?? []).filter(isFramework);
  const cases = (sources ?? []).filter(isCase);
  return (
    <div role="group" aria-labelledby={headingId}>
      <h4 id={headingId} style={{ margin: '0 0 var(--space-1)', fontSize: 'inherit', fontWeight: 600, color: 'var(--text)' }}>Sources</h4>
      <div role="status" aria-live="polite">
        {loading ? (
          <div style={{ color: 'var(--text-dim)' }}>Loading sources…</div>
        ) : error ? (
          <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', color: 'var(--text-muted)' }}>
            Could not load sources.
            {onRetry ? <Button variant="ghost" compact onClick={onRetry}>Retry</Button> : null}
          </div>
        ) : laws.length === 0 && frameworks.length === 0 && cases.length === 0 ? (
          <div style={{ color: 'var(--text-muted)' }}>
            No law or framework recorded for this control.{' '}
            <Link to={`/w/${encodeURIComponent(slug)}/app/settings/compliance`} style={linkStyle}>Frameworks in scope</Link>
          </div>
        ) : null}
      </div>
      {!loading && !error && (laws.length > 0 || frameworks.length > 0 || cases.length > 0) ? (
        <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
          {laws.length > 0 ? (
            <div style={{ display: 'grid', gap: 'var(--space-1)' }}>
              <h5 id={`${headingId}-laws`} style={groupHead}>Laws</h5>
              <ul aria-labelledby={`${headingId}-laws`} style={list}>
                {laws.map((s) => (
                  <li key={s.rule_id} style={row}>
                    <ExternalName name={s.short_name} url={s.url} />
                    {s.citation && s.citation !== s.short_name ? <span style={{ color: 'var(--text-dim)' }}>{s.citation}</span> : null}
                    <LawStatus s={s} slug={slug} back={back} />
                    {parameterText(s.parameters) ? <span style={{ color: 'var(--text-dim)', flexBasis: '100%' }}>{parameterText(s.parameters)}</span> : null}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {frameworks.length > 0 ? (
            <div style={{ display: 'grid', gap: 'var(--space-1)' }}>
              <h5 id={`${headingId}-frameworks`} style={groupHead}>Frameworks</h5>
              <ul aria-labelledby={`${headingId}-frameworks`} style={list}>
                {frameworks.map((f) => (
                  <li key={f.framework_id} style={row}>
                    <ExternalName name={f.version ? `${f.short_name} ${f.version}` : f.short_name} url={f.url} />
                    {f.requirements.length > 0 ? <span style={{ color: 'var(--text-dim)' }}>{f.requirements.map((r) => r.ref).join(' · ')}</span> : null}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {cases.length > 0 ? (
            <div style={{ display: 'grid', gap: 'var(--space-1)' }}>
              <h5 id={`${headingId}-cases`} style={groupHead}>Court cases</h5>
              <ul aria-labelledby={`${headingId}-cases`} style={list}>
                {cases.map((c) => (
                  <li key={c.case_id} style={row}>
                    <ExternalName name={c.short_name} url={c.url} />
                    <span style={{ color: 'var(--text-dim)' }}>{EFFECT_LABEL[c.effect]}</span>
                    <Badge>{caseStatusLabel(c)}</Badge>
                    <span style={{ color: 'var(--text-muted)', flexBasis: '100%' }}>
                      “{c.holding_excerpt}”{c.pinpoint ? <span style={{ color: 'var(--text-dim)' }}> · {c.pinpoint}</span> : null}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
